import React from "react";
import { motion } from "framer-motion";
import { PartyPopper, Lock, Pencil, Check, Radio as RadioIcon, Sparkles, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SIM_NAO_NAOSEI } from "@/lib/constants";

// --- helpers de horário (com virada de madrugada) ---
const parseMin = (t) => {
  if (!t || !/^\d{1,2}:\d{2}$/.test(t)) return null;
  const [h, m] = t.split(":").map(Number);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
};
const nightNorm = (min) => (min == null ? null : min < 720 ? min + 1440 : min); // antes do meio-dia = madrugada
const toInterval = (start, end) => {
  let s = nightNorm(parseMin(start));
  let e = nightNorm(parseMin(end));
  if (s == null || e == null) return null;
  if (e <= s) e += 1440;
  return [s, e];
};
const overlaps = (a, b) => a && b && a[0] < b[1] && b[0] < a[1];

const Radio = ({ options, value, onChange, testId }) => (
  <div className="mt-2 flex flex-wrap gap-2" data-testid={testId}>
    {options.map((o) => {
      const val = typeof o === "string" ? o : o.value;
      const lbl = typeof o === "string" ? o : o.label;
      const active = value === val;
      return (
        <button key={String(val)} type="button" onClick={() => onChange(val)}
          className={`rounded-full border px-3.5 py-2 text-sm transition-colors ${active ? "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)] text-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)] bg-white text-[color:var(--brand-graphite)] hover:bg-[color:var(--brand-paper)]"}`}>
          {lbl}
        </button>
      );
    })}
  </div>
);

// Horário simples confirmável (trava em somente leitura)
const TimeField = ({ label, value, confirmed, onValueChange, onConfirmChange, testIdBase }) => {
  const canConfirm = value && value.length >= 4;
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      {confirmed ? (
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span data-testid={`${testIdBase}-locked`} className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[color:var(--brand-graphite)] ring-1 ring-[color:var(--brand-border)]">
            <Lock className="h-3.5 w-3.5 text-[color:var(--brand-muted)]" /> {value || "—"}
          </span>
          <Button type="button" size="sm" variant="outline" data-testid={`${testIdBase}-edit`} onClick={() => onConfirmChange(false)} className="h-8"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar</Button>
        </div>
      ) : (
        <div className="mt-1.5 flex items-end gap-2">
          <Input data-testid={testIdBase} type="time" value={value || ""} onChange={(e) => onValueChange(e.target.value)} className="h-11 w-full sm:w-40" />
          <Button type="button" data-testid={`${testIdBase}-confirm`} disabled={!canConfirm} onClick={() => onConfirmChange(true)}
            className="h-11 shrink-0 bg-[color:var(--brand-marsala)] px-4 text-white hover:bg-[color:var(--brand-marsala-hover)] disabled:opacity-40"><Check className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Confirmar</span></Button>
        </div>
      )}
    </div>
  );
};

// Bloco de atração: Início + Fim, confirmável, com opção "a definir" e validação de sobreposição
const AttractionBlock = ({ label, icon: Icon, start, end, undefinedValue, confirmed, keys, onChange, onValidateConfirm }) => {
  const isUndefined = !!undefinedValue;
  const canConfirm = isUndefined || (start && end && start.length >= 4 && end.length >= 4);
  const set = (patch) => onChange(patch);

  const handleConfirm = () => {
    if (!isUndefined) {
      const ok = onValidateConfirm(start, end);
      if (!ok) return;
    }
    set({ [keys.confirmed]: true });
  };

  return (
    <div className={`rounded-xl border p-3.5 ${confirmed ? "border-[color:var(--brand-border)] bg-[color:var(--brand-paper)]" : "border-[color:var(--brand-marsala)] bg-[color:var(--brand-marsala-soft)]"}`}>
      <Label className="flex items-center gap-1.5">{Icon && <Icon className="h-4 w-4 text-[color:var(--brand-marsala)]" />} {label}</Label>

      {confirmed ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span data-testid={`${keys.base}-locked`} className="inline-flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[color:var(--brand-graphite)] ring-1 ring-[color:var(--brand-border)]">
            <Lock className="h-3.5 w-3.5 text-[color:var(--brand-muted)]" /> {isUndefined ? "Ainda não definido" : `${start || "--:--"} – ${end || "--:--"}`}
          </span>
          <Button type="button" size="sm" variant="outline" data-testid={`${keys.base}-edit`} onClick={() => set({ [keys.confirmed]: false })} className="h-8"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Editar</Button>
        </div>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <span className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-[color:var(--brand-muted)]">Início</span>
              <Input data-testid={`${keys.base}-inicio`} type="time" value={start || ""} disabled={isUndefined} onChange={(e) => set({ [keys.start]: e.target.value })} className="h-11 w-full" />
            </div>
            <div>
              <span className="mb-0.5 block text-[11px] font-medium uppercase tracking-wide text-[color:var(--brand-muted)]">Fim</span>
              <Input data-testid={`${keys.base}-fim`} type="time" value={end || ""} disabled={isUndefined} onChange={(e) => set({ [keys.end]: e.target.value })} className="h-11 w-full" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs text-[color:var(--brand-muted)]">
              <input type="checkbox" className="h-3.5 w-3.5 accent-[color:var(--brand-marsala)]" data-testid={`${keys.base}-undefined`} checked={isUndefined} onChange={(e) => set({ [keys.undef]: e.target.checked, ...(e.target.checked ? { [keys.start]: "", [keys.end]: "" } : {}) })} /> Ainda não definido
            </label>
            <Button type="button" data-testid={`${keys.base}-confirm`} disabled={!canConfirm} onClick={handleConfirm}
              className="h-10 bg-[color:var(--brand-marsala)] px-4 text-white hover:bg-[color:var(--brand-marsala-hover)] disabled:opacity-40"><Check className="mr-1.5 h-4 w-4" /> Confirmar horário</Button>
          </div>
        </>
      )}
    </div>
  );
};

export const FestaStep = ({ festa, services, onChange }) => {
  const f = festa || {};
  const svc = services || { banda: false, astrolive: false };
  const set = (patch) => onChange({ ...f, ...patch });

  // Coleta intervalos JÁ definidos das OUTRAS atrações (para impedir sobreposição)
  const otherIntervals = (exclude) => {
    const list = [];
    if (exclude !== "banda" && svc.banda && !f.show_banda_undefined) {
      const iv = toInterval(f.show_banda, f.show_banda_fim);
      if (iv) list.push({ nome: "Banda / Receptivo", iv });
    }
    if (exclude !== "astro" && svc.astrolive && !f.show_astrolive_undefined) {
      const iv = toInterval(f.show_astrolive, f.show_astrolive_fim);
      if (iv) list.push({ nome: "AstroLive", iv });
    }
    if (exclude !== "outra" && f.outra_atracao) {
      const iv = toInterval(f.outra_atracao_horario, f.outra_atracao_fim);
      if (iv) list.push({ nome: f.outra_atracao_nome || "Outra atração", iv });
    }
    return list;
  };

  const makeValidator = (which) => (start, end) => {
    const iv = toInterval(start, end);
    if (!iv) { toast.error("Preencha início e fim válidos."); return false; }
    const conflict = otherIntervals(which).find((o) => overlaps(iv, o.iv));
    if (conflict) {
      toast.error(`Este horário se sobrepõe ao show de ${conflict.nome}. Ajuste para não haver conflito.`);
      return false;
    }
    return true;
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="mx-auto max-w-2xl">
      <h2 className="flex items-center gap-2 font-serif text-2xl font-semibold text-[color:var(--brand-graphite)] sm:text-3xl"><PartyPopper className="h-6 w-6 text-[color:var(--brand-marsala)]" /> Cronograma da festa</h2>
      <p className="mt-2 text-sm text-[color:var(--brand-muted)]">Informe os horários do evento. Depois de confirmar, cada horário fica travado — mas você pode editar quando quiser.</p>

      {svc.banda && svc.astrolive && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[color:var(--brand-champagne)] bg-[color:var(--brand-champagne-soft)] p-3 text-sm text-[color:var(--brand-graphite)]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-marsala)]" />
          Você contratou <b className="mx-1">Banda / Receptivo</b> e <b className="mx-1">AstroLive</b> — são duas atrações distintas. Informe início e fim de cada show, sem sobreposição de horários.
        </div>
      )}

      {/* Início e fim da festa (juntos) */}
      <div className="mt-6 rounded-2xl border border-[color:var(--brand-border)] bg-white p-4">
        <h3 className="flex items-center gap-2 font-serif text-lg font-semibold text-[color:var(--brand-graphite)]"><Clock className="h-4 w-4 text-[color:var(--brand-marsala)]" /> Horário da festa (início e fim)</h3>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <TimeField label="Início da festa" testIdBase="festa-inicio" value={f.inicio} confirmed={!!f.inicio_confirmed} onValueChange={(v) => set({ inicio: v })} onConfirmChange={(v) => set({ inicio_confirmed: v })} />
          <TimeField label="Encerramento" testIdBase="festa-encerramento" value={f.encerramento} confirmed={!!f.encerramento_confirmed} onValueChange={(v) => set({ encerramento: v })} onConfirmChange={(v) => set({ encerramento_confirmed: v })} />
        </div>
      </div>

      {/* Horários das atrações (início + fim por atração) */}
      {(svc.banda || svc.astrolive) && (
        <div className="mt-6 space-y-3">
          <h3 className="font-serif text-lg font-semibold text-[color:var(--brand-graphite)]">Horários das atrações</h3>
          {svc.banda && (
            <AttractionBlock
              label="Show — Banda / Receptivo" icon={RadioIcon}
              start={f.show_banda} end={f.show_banda_fim} undefinedValue={f.show_banda_undefined} confirmed={!!f.show_banda_confirmed}
              keys={{ base: "festa-show-banda", start: "show_banda", end: "show_banda_fim", undef: "show_banda_undefined", confirmed: "show_banda_confirmed" }}
              onChange={set} onValidateConfirm={makeValidator("banda")}
            />
          )}
          {svc.astrolive && (
            <AttractionBlock
              label="Show — AstroLive" icon={Sparkles}
              start={f.show_astrolive} end={f.show_astrolive_fim} undefinedValue={f.show_astrolive_undefined} confirmed={!!f.show_astrolive_confirmed}
              keys={{ base: "festa-show-astrolive", start: "show_astrolive", end: "show_astrolive_fim", undef: "show_astrolive_undefined", confirmed: "show_astrolive_confirmed" }}
              onChange={set} onValidateConfirm={makeValidator("astro")}
            />
          )}
        </div>
      )}

      <div className="mt-6">
        <Label>Haverá outra atração no evento?</Label>
        <Radio testId="festa-outra" options={[{ value: true, label: "Sim" }, { value: false, label: "Não" }]} value={f.outra_atracao} onChange={(v) => set({ outra_atracao: v })} />
        {f.outra_atracao === true && (
          <div className="mt-3 space-y-3">
            <div><Label>Qual atração?</Label><Input data-testid="festa-outra-nome" value={f.outra_atracao_nome || ""} onChange={(e) => set({ outra_atracao_nome: e.target.value })} placeholder="DJ, Pagode, Cantor..." className="mt-1.5 h-11" /></div>
            <AttractionBlock
              label="Horário da outra atração"
              start={f.outra_atracao_horario} end={f.outra_atracao_fim} undefinedValue={false} confirmed={!!f.outra_atracao_confirmed}
              keys={{ base: "festa-outra-hora", start: "outra_atracao_horario", end: "outra_atracao_fim", undef: "outra_atracao_undef_unused", confirmed: "outra_atracao_confirmed" }}
              onChange={set} onValidateConfirm={makeValidator("outra")}
            />
          </div>
        )}
      </div>

      <div className="mt-6">
        <Label>Contratou sonorização conosco?</Label>
        <Radio testId="festa-sonorizacao" options={SIM_NAO_NAOSEI} value={f.sonorizacao} onChange={(v) => set({ sonorizacao: v })} />
      </div>

      <div className="mt-6">
        <Label>O local possui palco?</Label>
        <Radio testId="festa-palco" options={[{ value: "sim", label: "Sim" }, { value: "nao", label: "Não" }, { value: "nao_sei", label: "Não sei" }]} value={f.possui_palco} onChange={(v) => set({ possui_palco: v })} />
        {f.possui_palco === "sim" && (
          <div className="mt-3"><Label>Observações sobre o palco (opcional)</Label><Textarea data-testid="festa-palco-obs" value={f.palco_obs || ""} onChange={(e) => set({ palco_obs: e.target.value })} placeholder="Ex.: palco localizado ao lado da pista." className="mt-1.5" /></div>
        )}
      </div>
    </motion.div>
  );
};
